import { BodyText, Icon, IconSet } from '@postscript/components';
import React, { useState } from 'react';
import styled from 'styled-components';

import { ROW_LEFT_INSET } from './Row';

interface ExpandableRowProps {
  label: string;
  summary?: React.ReactNode;
  children: React.ReactNode;
}

// Mirrors the Row anchor surface (padding, border, hover/active) so the
// header reads as the same kind of interactive row, just with a chevron in
// place of a value. Rendered as a button since toggling expansion isn't a
// navigation. The `:not(:last-child)` border rule drops the bottom border
// when collapsed-and-last so it doesn't double up with the card's own.
const HeaderButton = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) ${ROW_LEFT_INSET};
  border: none;
  background: var(--surface-bkg-color);
  color: inherit;
  text-align: left;
  cursor: pointer;
  font: inherit;
  text-decoration: none;
  transition: background-color 80ms ease;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color-dim);
  }

  &,
  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    text-decoration: none;
  }

  &:hover {
    background: var(--gray-1);
  }

  &:active,
  &[data-pressed='true'] {
    background: var(--gray-2);
  }

  &:focus-visible {
    outline: 2px solid var(--link-color);
    outline-offset: -2px;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
`;

// `children` render inline below the header so they participate in the same
// flush card layout — each child should be a Row (or row-shaped element) so
// the dividers line up edge-to-edge.
const Body = styled.div`
  display: flex;
  flex-direction: column;
`;

// Right-aligned value stack for expansion bodies that render bare values
// (not full Rows). The padding mirrors `rowLayout` in Row.tsx so values line
// up with row content instead of sitting flush against the card's left edge.
export const ExpandableValueStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--spacing-1);
  padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) ${ROW_LEFT_INSET};
`;

export const ExpandableRow: React.FC<ExpandableRowProps> = ({
  label,
  summary,
  children,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <HeaderButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <BodyText size="small" bold>
          {label}
        </BodyText>
        <Right>
          {summary}
          <Icon
            component={open ? IconSet.ChevronUpLarge : IconSet.ChevronDownLarge}
            size={16}
          />
        </Right>
      </HeaderButton>
      {open && <Body>{children}</Body>}
    </>
  );
};
